import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  MapContainer,
  TileLayer,
  GeoJSON,
  Polygon,
  Polyline,
  Marker,
  Popup,
  Tooltip,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import { Box, Typography, Select, MenuItem, FormControl, FormControlLabel, Checkbox } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import "leaflet/dist/leaflet.css";
import { DOMParser } from "@xmldom/xmldom";
import { kml } from "@tmcw/togeojson";
import { FaCircleInfo } from "react-icons/fa6";
import { FaChevronLeft, FaChevronRight, FaCheck, FaTimes } from "react-icons/fa";
import CommonTable from "../../../common/CommonTable.js";
import { MapHomeControl, MapLegendControl } from "../../../common/MapControls/MapControls";

// ─── JTWC hardcoded cyclone data ───────────────────────────────────────────
const JTWC_CYCLONE_DATA = {
  1991: ["TC19S"],
  1995: ["TC21S"],
  1997: ["TC10S"],
  2002: ["TC22S"],
  2003: ["TC26S"],
  2011: ["ERROL"],
  2012: ["NINETEEN"],
  2014: ["GILLIAN"],
  2019: ["LILI"],
  2021: ["SEROJA"],
  2022: ["CHARLOTTE"],
};

const WP_LEAD_LABELS = {
  0: "0 hrs",
  1: "12 hrs",
  2: "24 hrs",
  3: "36 hrs",
  4: "48 hrs",
  5: "60 hrs",
  6: "72 hrs",
  7: "84 hrs",
};

// ─── Helpers ────────────────────────────────────────────────────────────────

// KML colour (AABBGGRR) → CSS rgba
const convertKMLColor = (kmlColor) => {
  if (!kmlColor) return "#000000";
  const hex = kmlColor.replace("#", "").trim();
  if (hex.length === 8) {
    const a = parseInt(hex.substring(0, 2), 16) / 255;
    const b = parseInt(hex.substring(2, 4), 16);
    const g = parseInt(hex.substring(4, 6), 16);
    const r = parseInt(hex.substring(6, 8), 16);
    return `rgba(${r},${g},${b},${a.toFixed(2)})`;
  }
  return `#${hex}`;
};

// TSR filename timestamp → readable date
// Formats: YYYYMMDDH{H} (10 chars) or YYYYMMDDH{HMM} (12 chars), month is 0-indexed
const formatAdvisoryTime = (ts) => {
  if (!ts || ts.length < 10) return ts;
  const year = parseInt(ts.substring(0, 4));
  const month0 = parseInt(ts.substring(4, 6));
  const day = parseInt(ts.substring(6, 8));
  const timePart = ts.substring(8);
  const hour = parseInt(timePart.substring(0, 2));
  const minute = timePart.length >= 4 ? parseInt(timePart.substring(2, 4)) : 0;
  const date = new Date(Date.UTC(year, month0, day, hour, minute));
  if (isNaN(date.getTime())) return ts;
  return (
    date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      timeZone: "UTC",
    }) +
    ` ${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")} UTC`
  );
};

// Extract advisory timestamp from a filename string (last underscore-separated part)
const extractTimestamp = (filename) => filename.split("_").pop();

// JTWC DTG formatter (existing logic)
const formatDTG = (dtg) => {
  if (!dtg || dtg === "N/A") return "N/A";
  const cleaned = dtg.replace("Z", "");
  if (cleaned.length < 10) return dtg;
  const year = cleaned.substring(0, 4);
  const month = cleaned.substring(4, 6);
  const day = cleaned.substring(6, 8);
  const hour = cleaned.substring(8, 10);
  const date = new Date(`${year}-${month}-${day}T${hour}:00:00Z`);
  if (isNaN(date.getTime())) return dtg;
  return (
    date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      timeZone: "UTC",
    }) + ` ${hour}:00 UTC`
  );
};

const formatDuration = (duration) => {
  if (!duration || duration === "N/A") return "N/A";
  const parts = duration.split("-");
  if (parts.length !== 2) return duration;
  const start = formatDTG(parts[0]);
  const end = formatDTG(parts[1]);
  if (start === "N/A" || end === "N/A") return duration;
  return `${start.replace(" UTC", "")} – ${end}`;
};

// ─── Column definitions for CommonTable (JTWC track table) ────────────────
const JTWC_TRACK_COLUMNS = [
  { key: "DTG", label: "DTG", sortable: true, align: "center", render: (row) => formatDTG(row.DTG) },
  { key: "Duration", label: "Duration", sortable: true, align: "center", render: (row) => formatDuration(row.Duration) },
  { key: "intensity", label: "Intensity", sortable: true, align: "center", render: (row) => `${row.intensity} kts` },
  { key: "mslp", label: "MSLP", sortable: true, align: "center" },
  { key: "maxIntensity", label: "Max Intensity", sortable: true, align: "center", render: (row) => `${row.maxIntensity} kts` },
  { key: "minSLP", label: "Min SLP", sortable: true, align: "center" },
];

// Fix antimeridian crossing (ported from DisturbanceTrack)
const fixAntimeridianFeatures = (features) => {
  const fixCoordSequence = (coords) => {
    if (!coords || coords.length < 2) return coords;
    const fixed = [coords[0].slice()];
    for (let i = 1; i < coords.length; i++) {
      const prev = fixed[i - 1];
      const curr = coords[i].slice();
      const diff = curr[0] - prev[0];
      if (diff > 180) curr[0] -= 360;
      else if (diff < -180) curr[0] += 360;
      fixed.push(curr);
    }
    return fixed;
  };
  const shiftCoords = (coords, delta) =>
    coords.map((c) => { const s = c.slice(); s[0] += delta; return s; });
  const getLngs = (geom) => {
    if (!geom) return [];
    if (geom.type === "Point") return [geom.coordinates[0]];
    if (geom.type === "LineString") return geom.coordinates.map((c) => c[0]);
    if (geom.type === "Polygon") return geom.coordinates.flatMap((r) => r.map((c) => c[0]));
    if (geom.type === "MultiPolygon") return geom.coordinates.flatMap((p) => p.flatMap((r) => r.map((c) => c[0])));
    return [];
  };
  const phase1 = features.map((f) => {
    if (!f?.geometry) return f;
    const g = f.geometry;
    if (g.type === "LineString")
      return { ...f, geometry: { ...g, coordinates: fixCoordSequence(g.coordinates) } };
    if (g.type === "Polygon")
      return { ...f, geometry: { ...g, coordinates: g.coordinates.map(fixCoordSequence) } };
    if (g.type === "MultiPolygon")
      return { ...f, geometry: { ...g, coordinates: g.coordinates.map((p) => p.map(fixCoordSequence)) } };
    return f;
  });
  let posHigh = false, negHigh = false;
  phase1.forEach((f) => {
    getLngs(f?.geometry).forEach((lng) => {
      if (lng > 100) posHigh = true;
      if (lng < -100) negHigh = true;
    });
  });
  if (!posHigh || !negHigh) return phase1;
  let posCount = 0, negCount = 0;
  phase1.forEach((f) => {
    const lngs = getLngs(f?.geometry);
    if (!lngs.length) return;
    if (lngs.reduce((a, b) => a + b, 0) / lngs.length >= 0) posCount++;
    else negCount++;
  });
  const delta = posCount >= negCount ? 360 : -360;
  return phase1.map((f) => {
    if (!f?.geometry) return f;
    const lngs = getLngs(f.geometry);
    if (!lngs.length) return f;
    const avg = lngs.reduce((a, b) => a + b, 0) / lngs.length;
    const shouldShift = posCount >= negCount ? avg < 0 : avg >= 0;
    if (!shouldShift) return f;
    const g = f.geometry;
    if (g.type === "LineString")
      return { ...f, geometry: { ...g, coordinates: shiftCoords(g.coordinates, delta) } };
    if (g.type === "Polygon")
      return { ...f, geometry: { ...g, coordinates: g.coordinates.map((r) => shiftCoords(r, delta)) } };
    if (g.type === "MultiPolygon")
      return { ...f, geometry: { ...g, coordinates: g.coordinates.map((p) => p.map((r) => shiftCoords(r, delta))) } };
    return f;
  });
};

// ─── Map helpers ─────────────────────────────────────────────────────────────

const InfoButton = ({ onClick }) => {
  // Leaflet's own drag/click handling swallows clicks on overlay controls
  // unless propagation to the map is explicitly disabled on the DOM node.
  const btnRef = useCallback((node) => {
    if (node) {
      L.DomEvent.disableClickPropagation(node);
      L.DomEvent.disableScrollPropagation(node);
    }
  }, []);
  return (
    <div
      ref={btnRef}
      style={{
        position: "absolute",
        bottom: "20px",
        left: "12px",
        padding: "8px",
        borderRadius: "8px",
        fontSize: "15px",
        cursor: "pointer",
        background: "rgba(255, 255, 255, 0.9)",
        backdropFilter: "blur(10px) brightness(1.1)",
        WebkitBackdropFilter: "blur(10px) brightness(1.1)",
        border: "1px solid rgba(0, 0, 0, 0.08)",
        color: "#333333",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
        transition: "all 0.2s ease",
        zIndex: 1000,
      }}
      onClick={onClick}
    >
      <FaCircleInfo />
    </div>
  );
};

// ─── Main component ──────────────────────────────────────────────────────────

const INITIAL_VIEW = { lat: -8.8742, lng: 125.7275, zoom: 6 };

// Fit to bounds but never zoom out past MIN_FIT_ZOOM (keeps every view readable)
const MIN_FIT_ZOOM = 6;
const fitBoundsWithMinZoom = (map, bounds, paddingPx = 50) => {
  const padding = L.point(paddingPx, paddingPx);
  const targetZoom = map.getBoundsZoom(bounds, false, padding);
  if (targetZoom < MIN_FIT_ZOOM) {
    map.setView([INITIAL_VIEW.lat, INITIAL_VIEW.lng], MIN_FIT_ZOOM, { animate: true });
  } else {
    map.fitBounds(bounds, { padding: [paddingPx, paddingPx], animate: true, duration: 1.5 });
  }
};

const FitBoundsToCyclone = ({ geoJsonData, isAllView }) => {
  const map = useMap();
  useEffect(() => {
    if (geoJsonData && geoJsonData.length > 0) {
      const combined = {
        type: "FeatureCollection",
        features: geoJsonData.flatMap((d) => d.features),
      };
      const layer = L.geoJson(combined);
      const bounds = layer.getBounds();
      if (!bounds.isValid()) return;
      if (isAllView) {
        map.setView([INITIAL_VIEW.lat, INITIAL_VIEW.lng], MIN_FIT_ZOOM, { animate: true });
      } else {
        fitBoundsWithMinZoom(map, bounds, 50);
      }
    }
  }, [geoJsonData, isAllView, map]);
  return null;
};

const FitBoundsToFeatures = ({ features }) => {
  const map = useMap();
  useEffect(() => {
    if (features && features.length > 0) {
      try {
        const layer = L.geoJson({ type: "FeatureCollection", features });
        const bounds = layer.getBounds();
        if (bounds.isValid()) fitBoundsWithMinZoom(map, bounds, 50);
      } catch (e) {
        // silent
      }
    }
  }, [features, map]);
  return null;
};

// ─── Main component ──────────────────────────────────────────────────────────

const HistoricalCyclone = () => {
  const [shapeData, setShapeData] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [info, setInfo] = useState(false);
  const infoRef = useRef(null);
  const infoPanelRef = useCallback((node) => {
    infoRef.current = node;
    if (node) {
      L.DomEvent.disableClickPropagation(node);
      L.DomEvent.disableScrollPropagation(node);
    }
  }, []);
  useEffect(() => {
    if (!info) return;
    const handleClickOutside = (e) => {
      if (infoRef.current && !infoRef.current.contains(e.target)) setInfo(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [info]);
  const modalRef = useRef(null);

  // ── Source ──
  const [source, setSource] = useState("JTWC"); // "JTWC" | "TSR"

  // ── JTWC states ──
  const [jtwcYear, setJtwcYear] = useState("all");
  const [jtwcCyclones, setJtwcCyclones] = useState([]);
  const [jtwcCyclone, setJtwcCyclone] = useState(""); // single selected cyclone
  const [geoJsonData, setGeoJsonData] = useState([]);
  const [trackData, setTrackData] = useState([]);
  const [selectedTrackIndex, setSelectedTrackIndex] = useState(null);
  const [showPopups, setShowPopups] = useState(false);
  const [showAllLabels, setShowAllLabels] = useState(false);
  const [forceUpdate, setForceUpdate] = useState(0);
  const [, setLoading] = useState(false);
  const [, setErrorMessage] = useState("");

  // ── TSR states ──
  const [tsrManifest, setTsrManifest] = useState(null);
  const [tsrYear, setTsrYear] = useState("");
  const [tsrBasin, setTsrBasin] = useState("");
  const [tsrCyclone, setTsrCyclone] = useState("");
  const [tsrSelectedProducts, setTsrSelectedProducts] = useState([]); // multi-select
  const [tsrLeadTime, setTsrLeadTime] = useState(0);
  const [tsrAdvisoryIndex, setTsrAdvisoryIndex] = useState(0);
  const [tsrAdvisories, setTsrAdvisories] = useState([]); // unique sorted timestamps
  const [tsrFeatures, setTsrFeatures] = useState([]);
  const [tsrStyles, setTsrStyles] = useState([]);
  const [loadingTSR, setLoadingTSR] = useState(false);

  // ── Prevent map scroll/zoom when pointer is over the modal ──
  useEffect(() => {
    const el = modalRef.current;
    if (!el) return;
    L.DomEvent.disableScrollPropagation(el);
  }, []);

  // ── Load shape boundary ──
  useEffect(() => {
    fetch("/DATA/OSMbased_adm0_boundary.json")
      .then((r) => r.json())
      .then(setShapeData)
      .catch(() => {});
  }, []);

  // ── Load TSR manifest ──
  useEffect(() => {
    fetch("/DATA/Cyclone/TSR/Historical/manifest.json")
      .then((r) => r.json())
      .then((data) => {
        setTsrManifest(data);
        const years = Object.keys(data).sort();
        if (years.length) {
          setTsrYear(years[years.length - 1]);
        }
      })
      .catch(() => {});
  }, []);

  // ── When TSR year changes → reset basin/cyclone ──
  useEffect(() => {
    if (!tsrManifest || !tsrYear) return;
    const basins = Object.keys(tsrManifest[tsrYear] || {}).sort();
    const firstBasin = basins[0] || "";
    setTsrBasin(firstBasin);
    setTsrCyclone("");
    setTsrFeatures([]);
    setTsrAdvisories([]);
  }, [tsrYear, tsrManifest]);

  // ── When TSR basin changes → reset cyclone ──
  useEffect(() => {
    if (!tsrManifest || !tsrYear || !tsrBasin) return;
    const cyclones = Object.keys(tsrManifest[tsrYear]?.[tsrBasin] || {}).sort();
    setTsrCyclone(cyclones[0] || "");
    setTsrFeatures([]);
    setTsrAdvisories([]);
  }, [tsrBasin, tsrYear, tsrManifest]);

  // ── When TSR cyclone changes → select all products, rebuild advisory list ──
  useEffect(() => {
    if (!tsrManifest || !tsrYear || !tsrBasin || !tsrCyclone) {
      setTsrAdvisories([]);
      setTsrSelectedProducts([]);
      return;
    }
    const cycloneEntry = tsrManifest[tsrYear]?.[tsrBasin]?.[tsrCyclone] || {};
    const available = Object.keys(cycloneEntry);
    setTsrSelectedProducts(available); // all selected by default

    // Build advisory timeline from primary product: GH > WP > RA
    const primary = available.includes("GH") ? "GH" : available[0];
    const files = cycloneEntry[primary] || [];
    const timestamps = [...new Set(files.map(extractTimestamp))].sort();
    setTsrAdvisories(timestamps);
    setTsrAdvisoryIndex(timestamps.length > 0 ? timestamps.length - 1 : 0);
  }, [tsrCyclone, tsrYear, tsrBasin, tsrManifest]);

  // ── Load TSR KML when advisory / selectedProducts / leadTime changes ──
  const loadTsrKml = useCallback(async () => {
    if (
      !tsrManifest ||
      !tsrYear ||
      !tsrBasin ||
      !tsrCyclone ||
      tsrAdvisories.length === 0 ||
      tsrSelectedProducts.length === 0
    )
      return;

    const timestamp = tsrAdvisories[tsrAdvisoryIndex];
    if (!timestamp) return;

    const cycloneEntry = tsrManifest[tsrYear]?.[tsrBasin]?.[tsrCyclone] || {};
    setLoadingTSR(true);

    const allFeatures = [];
    const allStyles = [];

    const parseKmlText = (text) => {
      const parser = new DOMParser();
      const kmlDoc = parser.parseFromString(text, "text/xml");
      const geojson = kml(kmlDoc);
      const valid = (geojson?.features || []).filter((f) => f?.geometry?.type);
      const styles = Array.from(kmlDoc.getElementsByTagName("Style")).map((style) => {
        const id = style.getAttribute("id");
        const lineEl = style.getElementsByTagName("LineStyle")[0];
        const polyEl = style.getElementsByTagName("PolyStyle")[0];
        let borderColor = "#000000";
        let fillColor = "rgba(255,255,255,0.3)";
        let weight = 1;
        if (lineEl) {
          const c = lineEl.getElementsByTagName("color")[0];
          const w = lineEl.getElementsByTagName("width")[0];
          if (c) borderColor = convertKMLColor(c.textContent);
          if (w) weight = Math.min(parseInt(w.textContent) || 1, 3);
        }
        if (polyEl) {
          const c = polyEl.getElementsByTagName("color")[0];
          if (c) fillColor = convertKMLColor(c.textContent);
        }
        return { id, borderColor, fillColor, weight };
      });
      return { valid, styles };
    };

    for (const product of tsrSelectedProducts) {
      const files = cycloneEntry[product] || [];
      let targetFiles = [];

      if (product === "GH") {
        const f = files.find((f) => extractTimestamp(f) === timestamp);
        if (f) targetFiles = [f];
      } else if (product === "WP") {
        const f = files.find((f) => {
          const parts = f.split("_");
          return parseInt(parts[1]) === tsrLeadTime && parts[parts.length - 1] === timestamp;
        });
        if (f) targetFiles = [f];
      } else {
        // RA and others: all files matching this timestamp
        targetFiles = files.filter((f) => extractTimestamp(f) === timestamp);
      }

      for (const targetFile of targetFiles) {
        const url = `/DATA/Cyclone/TSR/Historical/${tsrYear}/${tsrBasin}/${tsrCyclone}/${product}/${targetFile}.kml`;
        try {
          const res = await fetch(url);
          if (!res.ok) continue;
          const text = await res.text();
          const { valid, styles } = parseKmlText(text);
          allFeatures.push(...valid);
          allStyles.push(...styles);
        } catch (e) {
          console.error(`Error loading ${product} KML:`, e);
        }
      }
    }

    const fixed = fixAntimeridianFeatures(allFeatures);
    setTsrFeatures(fixed);
    setTsrStyles(allStyles);
    setLoadingTSR(false);
  }, [tsrManifest, tsrYear, tsrBasin, tsrCyclone, tsrSelectedProducts, tsrLeadTime, tsrAdvisoryIndex, tsrAdvisories]);

  useEffect(() => {
    if (source === "TSR") loadTsrKml();
  }, [source, loadTsrKml]);

  // ── JTWC: year changes → auto-select first cyclone ──
  useEffect(() => {
    if (source !== "JTWC") return;
    if (jtwcYear === "all") {
      setJtwcCyclones([]);
      setJtwcCyclone("");
      setGeoJsonData([]);
      setTrackData([]);
      loadJtwcAll();
    } else {
      const cyclones = JTWC_CYCLONE_DATA[jtwcYear] || [];
      setJtwcCyclones(cyclones);
      setJtwcCyclone(cyclones[0] || "");
      setGeoJsonData([]);
      setTrackData([]);
    }
    setErrorMessage("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jtwcYear, source]);

  // ── JTWC: cyclone selection changes → load single cyclone ──
  useEffect(() => {
    if (source !== "JTWC" || jtwcYear === "all" || !jtwcCyclone) return;
    loadJtwcSingle(jtwcYear, jtwcCyclone);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jtwcCyclone]);

  const loadJtwcAll = async () => {
    setLoading(true);
    const allData = [];
    const allPoints = [];
    for (const [year, cyclones] of Object.entries(JTWC_CYCLONE_DATA)) {
      for (const cyclone of cyclones) {
        const path = `/DATA/Cyclone/JTWC_Historical_Cyclones/${year} - ${cyclone}.geojson`;
        try {
          const res = await fetch(path);
          if (!res.ok) continue;
          const data = await res.json();
          allData.push(data);
          allPoints.push(
            ...data.features
              .filter((f) => f.geometry.type === "Point")
              .map((f) => ({
                DTG: f.properties["DTG"] || "N/A",
                Duration: f.properties["Duration"] || "N/A",
                intensity: f.properties["Intensity"] || "N/A",
                mslp: f.properties["MSLP"] || "N/A",
                maxIntensity: f.properties["Maximum Intensity"] || "N/A",
                minSLP: f.properties["Minimum SLP"] || "N/A",
              }))
          );
        } catch (e) {
          // silent
        }
      }
    }
    setGeoJsonData(allData);
    setTrackData(allPoints.map((p, idx) => ({ ...p, rowKey: idx })));
    setLoading(false);
  };

  const loadJtwcSingle = async (year, cyclone) => {
    setLoading(true);
    const path = `/DATA/Cyclone/JTWC_Historical_Cyclones/${year} - ${cyclone}.geojson`;
    try {
      const res = await fetch(path);
      if (res.ok) {
        const data = await res.json();
        const points = data.features
          .filter((f) => f.geometry.type === "Point")
          .map((f, idx) => ({
            DTG: f.properties["DTG"] || "N/A",
            Duration: f.properties["Duration"] || "N/A",
            intensity: f.properties["Intensity"] || "N/A",
            mslp: f.properties["MSLP"] || "N/A",
            maxIntensity: f.properties["Maximum Intensity"] || "N/A",
            minSLP: f.properties["Minimum SLP"] || "N/A",
            rowKey: idx,
          }));
        setGeoJsonData([data]);
        setTrackData(points);
      }
    } catch (e) {
      // silent
    }
    setErrorMessage("");
    setLoading(false);
  };

  const togglePopups = () => {
    setShowPopups((p) => !p);
    setForceUpdate((p) => p + 1);
  };

  // ── Derived values ──
  const tsrYears = tsrManifest ? Object.keys(tsrManifest).sort() : [];
  const tsrBasins = tsrManifest && tsrYear ? Object.keys(tsrManifest[tsrYear] || {}).sort() : [];
  const tsrCycloneList =
    tsrManifest && tsrYear && tsrBasin
      ? Object.keys(tsrManifest[tsrYear]?.[tsrBasin] || {}).sort()
      : [];
  const tsrProducts =
    tsrManifest && tsrYear && tsrBasin && tsrCyclone
      ? Object.keys(tsrManifest[tsrYear]?.[tsrBasin]?.[tsrCyclone] || {})
      : [];
  const currentAdvisoryLabel =
    tsrAdvisories.length > 0
      ? formatAdvisoryTime(tsrAdvisories[tsrAdvisoryIndex])
      : "—";

  // ── Info box derived values ──
  const infoBox = (() => {
    if (source === "TSR" && tsrCyclone && tsrAdvisories.length > 0) {
      return {
        show: true,
        basin: tsrBasin,
        cyclone: tsrCyclone.replace("-JTWC", "").replace("-La-Reunion", " (La Réunion)"),
        period: `${formatAdvisoryTime(tsrAdvisories[0])} – ${formatAdvisoryTime(tsrAdvisories[tsrAdvisories.length - 1])}`,
      };
    }
    if (source === "JTWC" && trackData.length > 0) {
      const firstDTG = trackData[0]?.DTG;
      const lastDTG = trackData[trackData.length - 1]?.DTG;
      return {
        show: true,
        basin: null,
        cyclone: jtwcCyclone || "All",
        period: firstDTG && lastDTG ? `${formatDTG(firstDTG)} – ${formatDTG(lastDTG)}` : "—",
      };
    }
    return { show: false };
  })();

  // ── MUI Select sx — matches DisturbanceTrack exactly ──
  const muiSelectSx = {
    backgroundColor: "rgba(255, 255, 255, 0.15)",
    color: "white",
    borderRadius: "6px",
    fontSize: "14px",
    height: "42px",
    border: "1px solid rgba(255, 255, 255, 0.25)",
    transition: "all 0.2s ease",
    "& .MuiOutlinedInput-notchedOutline": { border: "none" },
    "& .MuiSelect-icon": { color: "white" },
    "&:hover": { borderColor: "rgba(255,255,255,0.5)", boxShadow: "0 2px 8px rgba(255,255,255,0.1)" },
    "&.Mui-focused": { borderColor: "rgba(255,255,255,0.5)", boxShadow: "0 4px 12px rgba(255,255,255,0.15)" },
  };
  const muiMenuProps = { sx: { zIndex: 2000 } };

  return (
    <div style={{ flexDirection: "column", alignItems: "center" }}>
      {/* ── Map ── */}
      <div style={{ borderRadius: "10px", overflow: "hidden", position: "relative" }}>
        <MapContainer
          center={[INITIAL_VIEW.lat, INITIAL_VIEW.lng]}
          zoom={INITIAL_VIEW.zoom}
          className="mapcontainerall"
        >
          <MapHomeControl />
          {/* Info tooltip */}
          {info && (
            <div
              ref={infoPanelRef}
              style={{
                position: "absolute",
                bottom: "70px",
                left: "12px",
                fontSize: "12px",
                zIndex: 1000,
                padding: "10px 14px",
                borderRadius: "10px",
                background: "rgba(255, 255, 255, 0.9)",
                backdropFilter: "blur(10px) brightness(1.1)",
                WebkitBackdropFilter: "blur(10px) brightness(1.1)",
                border: "1px solid rgba(0, 0, 0, 0.08)",
                boxShadow: "0 4px 16px rgba(0, 0, 0, 0.15), inset 0 1px 2px rgba(255, 255, 255, 0.5)",
                color: "#333333",
                fontWeight: "500",
                maxWidth: "320px",
                lineHeight: "1.4",
              }}
            >
              {source === "TSR"
                ? "Historical cyclone data from TSR (Tropical Storm Risk). Select year, basin, cyclone and step through advisories."
                : "Historical cyclone track data sourced from JTWC."}
            </div>
          )}
          <InfoButton onClick={() => setInfo((v) => !v)} />

          <TileLayer
            url="https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}"
            attribution=""
          />

          {/* Boundary overlay */}
          {shapeData && (
            <GeoJSON
              data={shapeData}
              style={{ color: "#333333", weight: 2, fillOpacity: 0.1 }}
            />
          )}

          {/* Show Intensity toggle (JTWC only) */}
          {source === "JTWC" && (
            <div style={{ position: "absolute", bottom: "20px", left: "56px", zIndex: 1000 }}>
              <button
                onClick={togglePopups}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "8px 14px",
                  background: "rgba(30, 41, 59, 0.45)",
                  backdropFilter: "blur(10px)",
                  WebkitBackdropFilter: "blur(10px)",
                  color: "#ffffff",
                  border: "1px solid rgba(255,255,255,0.2)",
                  borderRadius: "10px",
                  cursor: "pointer",
                  fontSize: "12px",
                  fontWeight: "600",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
                  transition: "all 0.2s ease",
                }}
              >
                {showPopups ? <FaTimes style={{ fontSize: 14 }} /> : <FaCheck style={{ fontSize: 14 }} />}
                {showPopups ? "Hide Intensity" : "Show Intensity"}
              </button>
            </div>
          )}

          {/* TSR: Show/Hide All Labels toggle */}
          {source === "TSR" && tsrFeatures.length > 0 && (
            <div style={{ position: "absolute", bottom: "20px", left: "56px", zIndex: 1000 }}>
              <button
                onClick={() => setShowAllLabels((prev) => !prev)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "8px 14px",
                  background: "rgba(30, 41, 59, 0.45)",
                  backdropFilter: "blur(10px) brightness(1.1)",
                  WebkitBackdropFilter: "blur(10px) brightness(1.1)",
                  color: "#ffffff",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  borderRadius: "10px",
                  cursor: "pointer",
                  fontSize: "12px",
                  fontWeight: "600",
                  boxShadow: "0 4px 16px rgba(0, 0, 0, 0.25), inset 0 1px 2px rgba(255, 255, 255, 0.15)",
                  transition: "all 0.2s ease",
                  letterSpacing: "0.3px",
                }}
                title={showAllLabels ? "Hide all labels" : "Show all labels"}
              >
                {showAllLabels ? <CloseIcon style={{ fontSize: 16 }} /> : <CheckIcon style={{ fontSize: 16 }} />}
                {showAllLabels ? "Hide All Labels" : "Show All Labels"}
              </button>
            </div>
          )}

          {/* TSR loading indicator */}
          {source === "TSR" && loadingTSR && (
            <div
              style={{
                position: "absolute",
                bottom: "30px",
                left: "12px",
                zIndex: 1000,
                background: "rgba(25,118,210,0.85)",
                color: "#ffffff",
                padding: "8px 14px",
                borderRadius: "10px",
                fontSize: "12px",
                fontWeight: "600",
              }}
            >
              Loading advisory…
            </div>
          )}

          {/* Slide modal toggle button */}
          <button
            onClick={() => setShowModal((v) => !v)}
            style={{
              position: "absolute",
              top: "50%",
              right: showModal ? "300px" : "20px",
              transform: "translateY(-50%)",
              zIndex: 1001,
              backgroundColor: "#1976d2",
              border: "none",
              borderRadius: "6px",
              width: "50px",
              height: "50px",
              fontSize: "20px",
              cursor: "pointer",
              boxShadow: "0 4px 12px rgba(25,118,210,0.3)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.3s ease",
              color: "white",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = "0 6px 16px rgba(25,118,210,0.4)";
              e.currentTarget.style.transform = "translateY(-50%) scale(1.05)";
              e.currentTarget.style.backgroundColor = "#1565c0";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = "0 4px 12px rgba(25,118,210,0.3)";
              e.currentTarget.style.transform = "translateY(-50%) scale(1)";
              e.currentTarget.style.backgroundColor = "#1976d2";
            }}
          >
            {showModal ? <FaChevronRight /> : <FaChevronLeft />}
          </button>

          {/* ── Sliding Modal ── */}
          <div
            ref={modalRef}
            style={{
              position: "absolute",
              right: showModal ? "0" : "-300px",
              top: 0,
              height: "100%",
              width: "300px",
              background: "rgba(255, 255, 255, 0.15)",
              backdropFilter: "blur(12px) brightness(1.1)",
              WebkitBackdropFilter: "blur(12px) brightness(1.1)",
              boxShadow: "-4px 0 16px rgba(0, 0, 0, 0.15), inset 0 1px 2px rgba(255, 255, 255, 0.3)",
              zIndex: 1000,
              transition: "right 0.3s ease",
              padding: "24px 20px",
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              borderLeft: "1px solid rgba(255, 255, 255, 0.25)",
            }}
          >
            {/* ── Source selector ── */}
            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 0, fontSize: "15px", color: "white" }}>
              Source
            </Typography>
            <Box sx={{ display: "flex", gap: "8px", mt: -1 }}>
              {["JTWC", "TSR"].map((s) => (
                <button
                  key={s}
                  onClick={() => {
                    setSource(s);
                    setTsrFeatures([]);
                    setGeoJsonData([]);
                    setTrackData([]);
                    setJtwcCyclone("");
                  }}
                  style={{
                    flex: 1,
                    padding: "7px 0",
                    borderRadius: "6px",
                    border: "1px solid rgba(255,255,255,0.3)",
                    background: source === s ? "#1976d2" : "rgba(255,255,255,0.15)",
                    color: "white",
                    fontWeight: "600",
                    fontSize: "13px",
                    cursor: "pointer",
                    transition: "background 0.2s",
                  }}
                >
                  {s}
                </button>
              ))}
            </Box>

            {/* ── JTWC controls ── */}
            {source === "JTWC" && (
              <>
                <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1, fontSize: "15px", color: "white" }}>
                  Select Year
                </Typography>
                <FormControl fullWidth sx={{ mt: -1 }}>
                  <Select
                    value={jtwcYear}
                    onChange={(e) => setJtwcYear(e.target.value)}
                    MenuProps={muiMenuProps}
                    sx={muiSelectSx}
                  >
                    <MenuItem value="all">All</MenuItem>
                    {Object.keys(JTWC_CYCLONE_DATA).map((y) => (
                      <MenuItem key={y} value={y}>{y}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {jtwcYear !== "all" && (
                  <>
                    <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1, fontSize: "15px", color: "white" }}>
                      Select Cyclone
                    </Typography>
                    <FormControl fullWidth sx={{ mt: -1 }}>
                      <Select
                        value={jtwcCyclone}
                        onChange={(e) => setJtwcCyclone(e.target.value)}
                        MenuProps={muiMenuProps}
                        sx={muiSelectSx}
                      >
                        {jtwcCyclones.map((c) => (
                          <MenuItem key={c} value={c}>{c}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </>
                )}
              </>
            )}

            {/* ── TSR controls ── */}
            {source === "TSR" && (
              <>
                {/* Year */}
                <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1, fontSize: "15px", color: "white" }}>
                  Select Year
                </Typography>
                <FormControl fullWidth sx={{ mt: -1 }}>
                  <Select
                    value={tsrYear}
                    onChange={(e) => setTsrYear(e.target.value)}
                    MenuProps={muiMenuProps}
                    sx={muiSelectSx}
                  >
                    {tsrYears.map((y) => (
                      <MenuItem key={y} value={y}>{y}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Basin */}
                <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1, fontSize: "15px", color: "white" }}>
                  Select Basin
                </Typography>
                <FormControl fullWidth sx={{ mt: -1 }}>
                  <Select
                    value={tsrBasin}
                    onChange={(e) => setTsrBasin(e.target.value)}
                    MenuProps={muiMenuProps}
                    sx={muiSelectSx}
                  >
                    {tsrBasins.map((b) => (
                      <MenuItem key={b} value={b}>
                        {b === "SIO" ? "South Indian Ocean (SIO)" : "South West Pacific (SWP)"}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Cyclone */}
                <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1, fontSize: "15px", color: "white" }}>
                  Select Cyclone
                </Typography>
                <FormControl fullWidth sx={{ mt: -1 }}>
                  <Select
                    value={tsrCyclone}
                    onChange={(e) => { setTsrCyclone(e.target.value); setTsrFeatures([]); }}
                    MenuProps={muiMenuProps}
                    sx={muiSelectSx}
                  >
                    {tsrCycloneList.map((c) => (
                      <MenuItem key={c} value={c}>
                        {c.replace("-JTWC", " (JTWC)").replace("-La-Reunion", " (La Réunion)").replace("-BOM"," (BOM)")}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Active Display – multi-select checkboxes, all on by default */}
                {tsrProducts.length > 0 && (
                  <>
                    <Typography variant="h6" sx={{ fontWeight: "bold", mt: 1, mb: 1, fontSize: "15px", color: "white" }}>
                      Active Display
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", mt: -1 }}>
                      {tsrProducts.map((p) => {
                        const LABELS = { GH: "Gust History", WP: "Wind Probabilities", RA: "GFS Rainfall" };
                        return (
                          <FormControlLabel
                            key={p}
                            control={
                              <Checkbox
                                size="small"
                                checked={tsrSelectedProducts.includes(p)}
                                onChange={() =>
                                  setTsrSelectedProducts((prev) =>
                                    prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]
                                  )
                                }
                                sx={{ color: "rgba(255,255,255,0.7)", "&.Mui-checked": { color: "white" } }}
                              />
                            }
                            label={LABELS[p] || p}
                            sx={{ color: "white", "& .MuiFormControlLabel-label": { fontSize: "14px" } }}
                          />
                        );
                      })}
                    </Box>
                  </>
                )}

                {/* WP lead time */}
                {tsrSelectedProducts.includes("WP") && (
                  <>
                    <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1, fontSize: "15px", color: "white" }}>
                      Lead Time
                    </Typography>
                    <FormControl fullWidth sx={{ mt: -1 }}>
                      <Select
                        value={tsrLeadTime}
                        onChange={(e) => setTsrLeadTime(parseInt(e.target.value))}
                        MenuProps={muiMenuProps}
                        sx={muiSelectSx}
                      >
                        {Object.entries(WP_LEAD_LABELS).map(([val, label]) => (
                          <MenuItem key={val} value={Number(val)}>{label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </>
                )}

                {/* Advisory slider */}
                {tsrAdvisories.length > 0 && (
                  <>
                    <Typography variant="h6" sx={{ fontWeight: "bold", mb: 0, fontSize: "15px", color: "white" }}>
                      Advisory
                    </Typography>
                    <Typography sx={{ fontSize: "13px", color: "rgba(255,255,255,0.9)", fontWeight: 500, mt: -1 }}>
                      {currentAdvisoryLabel}
                    </Typography>
                    <Box sx={{ mt: -1 }}>
                      <input
                        type="range"
                        min={0}
                        max={tsrAdvisories.length - 1}
                        value={tsrAdvisoryIndex}
                        onChange={(e) => setTsrAdvisoryIndex(parseInt(e.target.value))}
                        style={{ width: "100%", accentColor: "#1976d2", cursor: "pointer" }}
                      />
                      <Box sx={{ display: "flex", justifyContent: "space-between", mt: "2px" }}>
                        <Typography sx={{ fontSize: "10px", color: "rgba(255,255,255,0.6)" }}>
                          {formatAdvisoryTime(tsrAdvisories[0])?.split(",")[0] || "Start"}
                        </Typography>
                        <Typography sx={{ fontSize: "10px", color: "rgba(255,255,255,0.6)" }}>
                          {formatAdvisoryTime(tsrAdvisories[tsrAdvisories.length - 1])?.split(",")[0] || "End"}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Prev / Next */}
                    <Box sx={{ display: "flex", gap: "8px", mt: -1 }}>
                      <button
                        onClick={() => setTsrAdvisoryIndex((i) => Math.max(0, i - 1))}
                        disabled={tsrAdvisoryIndex === 0}
                        style={{
                          flex: 1, padding: "6px", borderRadius: "6px",
                          border: "1px solid rgba(255,255,255,0.25)",
                          background: "rgba(255,255,255,0.15)", color: "white",
                          cursor: "pointer", fontSize: "13px", display: "flex",
                          alignItems: "center", justifyContent: "center", gap: "4px",
                          opacity: tsrAdvisoryIndex === 0 ? 0.4 : 1,
                        }}
                      >
                        <FaChevronLeft /> Prev
                      </button>
                      <button
                        onClick={() => setTsrAdvisoryIndex((i) => Math.min(tsrAdvisories.length - 1, i + 1))}
                        disabled={tsrAdvisoryIndex === tsrAdvisories.length - 1}
                        style={{
                          flex: 1, padding: "6px", borderRadius: "6px",
                          border: "1px solid rgba(255,255,255,0.25)",
                          background: "rgba(255,255,255,0.15)", color: "white",
                          cursor: "pointer", fontSize: "13px", display: "flex",
                          alignItems: "center", justifyContent: "center", gap: "4px",
                          opacity: tsrAdvisoryIndex === tsrAdvisories.length - 1 ? 0.4 : 1,
                        }}
                      >
                        Next <FaChevronRight />
                      </button>
                    </Box>

                    <Typography sx={{ fontSize: "11px", color: "rgba(255,255,255,0.5)", textAlign: "center", mt: -1 }}>
                      {tsrAdvisoryIndex + 1} / {tsrAdvisories.length}
                    </Typography>
                  </>
                )}
              </>
            )}
          </div>

          {/* ── JTWC: fit map ── */}
          {source === "JTWC" && (
            <FitBoundsToCyclone geoJsonData={geoJsonData} isAllView={jtwcYear === "all"} />
          )}

          {/* ── TSR: fit map ── */}
          {source === "TSR" && tsrFeatures.length > 0 && (
            <FitBoundsToFeatures features={tsrFeatures} />
          )}

          {/* ── JTWC GeoJSON layer ── */}
          {source === "JTWC" && geoJsonData.length > 0 && (
            <GeoJSON
              key={forceUpdate}
              data={{
                type: "FeatureCollection",
                features: geoJsonData.flatMap((d) => d.features),
              }}
              style={(feature) => ({
                color: feature.geometry.type === "LineString" ? "black" : "transparent",
                weight: feature.geometry.type === "LineString" ? 1 : 0,
              })}
              pointToLayer={(feature, latlng) => {
                if (feature.geometry.type !== "Point") return null;
                const marker = L.marker(latlng, {
                  icon: L.icon({
                    iconUrl: "/DATA/Cyclone/images/black.png",
                    iconSize: [20, 20],
                    iconAnchor: [10, 10],
                  }),
                });
                const { DTG, Duration } = feature.properties;
                const intensity = feature.properties["Intensity"] || "N/A";
                const mslp = feature.properties["MSLP"] || "N/A";
                const maxIntensity = feature.properties["Maximum Intensity"] || "N/A";
                const minSLP = feature.properties["Minimum SLP"] || "N/A";
                if (showPopups) {
                  marker.bindTooltip(
                    `<div style="font-size:11px;min-width:120px;">
                      <div style="font-weight:700;color:#1565c0;margin-bottom:3px;border-bottom:1px solid #e0e0e0;padding-bottom:3px;">${formatDTG(DTG)}</div>
                      <div><strong>Intensity:</strong> ${intensity} kts</div>
                      ${mslp !== "N/A" ? `<div><strong>MSLP:</strong> ${mslp}</div>` : ""}
                    </div>`,
                    { permanent: true, direction: "right", opacity: 0.95, className: "custom-tooltip" }
                  );
                }
                marker.bindPopup(`
                  <div style="max-width:280px;border-radius:4px;overflow:hidden;">
                    <h4 style="margin:0;font-weight:bold;font-size:14px;background:#007bff;color:white;padding:8px;border-radius:4px 4px 0 0;">Cyclone Track Point</h4>
                    <table style="width:100%;border-collapse:collapse;background:#e3f2fd;font-size:12px;">
                      <tbody>
                        <tr><td style="border:1px solid #ddd;padding:5px;font-weight:bold;color:#1565c0;">DTG</td><td style="border:1px solid #ddd;padding:5px;">${formatDTG(DTG)}</td></tr>
                        <tr><td style="border:1px solid #ddd;padding:5px;font-weight:bold;color:#1565c0;">Duration</td><td style="border:1px solid #ddd;padding:5px;">${formatDuration(Duration)}</td></tr>
                        <tr><td style="border:1px solid #ddd;padding:5px;font-weight:bold;color:#1565c0;">Intensity</td><td style="border:1px solid #ddd;padding:5px;">${intensity} kts</td></tr>
                        <tr><td style="border:1px solid #ddd;padding:5px;font-weight:bold;color:#1565c0;">MSLP</td><td style="border:1px solid #ddd;padding:5px;">${mslp} hPa</td></tr>
                        <tr><td style="border:1px solid #ddd;padding:5px;font-weight:bold;color:#1565c0;">Max Intensity</td><td style="border:1px solid #ddd;padding:5px;">${maxIntensity} kts</td></tr>
                        <tr><td style="border:1px solid #ddd;padding:5px;font-weight:bold;color:#1565c0;">Min SLP</td><td style="border:1px solid #ddd;padding:5px;">${minSLP} hPa</td></tr>
                      </tbody>
                    </table>
                  </div>`);
                marker.on("click", () => {
                  const idx = trackData.findIndex(
                    (t) => t.Duration === Duration && t.DTG === DTG
                  );
                  setSelectedTrackIndex(idx);
                });
                return marker;
              }}
            />
          )}

          {/* ── TSR KML layer ── */}
          {source === "TSR" && tsrFeatures.length > 0 && tsrFeatures.map((feature, idx) => {
            if (!feature?.geometry?.type) return null;
            const styleUrl = (feature.properties?.styleUrl || "").replace("#", "");
            const style = tsrStyles.find((s) => s.id === styleUrl) || {};

            if (feature.geometry.type === "Polygon") {
              const positions = feature.geometry.coordinates.map((ring) =>
                ring.map((coord) => [coord[1], coord[0]])
              );
              const name = feature.properties?.name || "";
              const desc = feature.properties?.description || "";
              return (
                <Polygon
                  key={`tsr-poly-${idx}`}
                  positions={positions}
                  pathOptions={{
                    color: style.borderColor || "#1565c0",
                    weight: 2,
                    opacity: 0.8,
                    fillColor: style.fillColor || "#42a5f5",
                    fillOpacity: 0.4,
                  }}
                  eventHandlers={{
                    click: (e) => {
                      e.target.bindPopup(
                        `<div style="font-family:sans-serif;font-size:13px;">
                          <div style="background:#007bff;color:#fff;padding:6px 10px;font-weight:bold;border-radius:4px 4px 0 0;">${name || "TSR Zone"}</div>
                          <div style="background:#e3f2fd;padding:8px 10px;border-radius:0 0 4px 4px;color:#1565c0;">${desc || ""}</div>
                        </div>`
                      ).openPopup();
                    },
                  }}
                />
              );
            }

            if (feature.geometry.type === "LineString") {
              const positions = feature.geometry.coordinates.map((coord) => [coord[1], coord[0]]);
              const name = feature.properties?.name || "";
              return (
                <Polyline
                  key={`tsr-line-${idx}`}
                  positions={positions}
                  pathOptions={{
                    color: style.borderColor || "#FF0000",
                    weight: 3,
                    opacity: 0.8,
                    lineCap: "round",
                    lineJoin: "round",
                  }}
                >
                  {name && <Popup>{name}</Popup>}
                </Polyline>
              );
            }

            if (feature.geometry.type === "Point") {
              const [lng, lat] = feature.geometry.coordinates;
              const props = feature.properties || {};
              const name = props.name || "";
              const rows = [
                ["Name", name],
                ["Date", props["Date"] || props["TimeStamp"] || ""],
                ["Wind Speed", props["Maximum 10-min Sustained Wind"] || props["Wind"] || ""],
                ["Storm Category", props["Storm Category"] || props["Category"] || ""],
                ["Storm Position", props["Storm Position"] || props["Position"] || ""],
                ["Description", props["description"] || ""],
              ].filter(([, v]) => v);

              const icon = L.divIcon({
                className: "",
                html: `<div style="width:10px;height:10px;background:#1976d2;border:2px solid #fff;border-radius:50%;box-shadow:0 0 4px rgba(0,0,0,0.4);"></div>`,
                iconSize: [10, 10],
                iconAnchor: [5, 5],
              });

              return (
                <Marker key={`tsr-pt-${idx}`} position={[lat, lng]} icon={icon}>
                  <Popup>
                    <div style={{ fontFamily: "sans-serif", fontSize: "13px", minWidth: "180px" }}>
                      <div style={{ background: "#007bff", color: "#fff", padding: "6px 10px", fontWeight: "bold", borderRadius: "4px 4px 0 0", textAlign: "center" }}>
                        {name || "TSR Point"}
                      </div>
                      <table style={{ borderCollapse: "collapse", width: "100%", background: "#e3f2fd" }}>
                        <tbody>
                          {rows.map(([label, value]) => (
                            <tr key={label}>
                              <td style={{ border: "1px solid #bbdefb", padding: "4px 8px", fontWeight: "bold", color: "#1565c0", width: "45%" }}>{label}</td>
                              <td style={{ border: "1px solid #bbdefb", padding: "4px 8px", color: "#0d47a1" }}>{value}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Popup>
                  {showAllLabels && (
                    <Tooltip permanent direction="top" offset={[0, -8]} className="cyclone-permanent-tooltip">
                      <div style={{ fontSize: "11px", lineHeight: "1.4", minWidth: "120px" }}>
                        <div style={{ fontWeight: "700", color: "#1565c0", marginBottom: "3px", borderBottom: "1px solid #e0e0e0", paddingBottom: "3px" }}>
                          {name || "N/A"}
                        </div>
                        {props["Storm Category"] && (
                          <div><strong>Cat:</strong> {props["Storm Category"]}</div>
                        )}
                        {(props["Date"] || props["TimeStamp"]) && (
                          <div><strong>Date:</strong> {props["Date"] || props["TimeStamp"]}</div>
                        )}
                        {(props["Maximum 10-min Sustained Wind"] || props["Wind"]) && (
                          <div><strong>Wind:</strong> {props["Maximum 10-min Sustained Wind"] || props["Wind"]}</div>
                        )}
                      </div>
                    </Tooltip>
                  )}
                </Marker>
              );
            }

            return null;
          })}

          {/* ── Cyclone info box – top left ── */}
          {infoBox.show && (
            <div
              style={{
                position: "absolute",
                top: "12px",
                left: "52px",
                zIndex: 1000,
                background: "rgba(30, 41, 59, 0.45)",
                backdropFilter: "blur(12px) brightness(1.1)",
                WebkitBackdropFilter: "blur(12px) brightness(1.1)",
                border: "1px solid rgba(255, 255, 255, 0.2)",
                borderRadius: "10px",
                padding: "10px 14px",
                boxShadow: "0 4px 16px rgba(0, 0, 0, 0.25), inset 0 1px 2px rgba(255, 255, 255, 0.15)",
                maxWidth: "240px",
              }}
            >
              {infoBox.basin && (
                <div style={{ display: "flex", gap: "6px", marginBottom: "4px", alignItems: "baseline" }}>
                  <span style={{ fontSize: "10px", color: "rgba(255,255,255,0.6)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", whiteSpace: "nowrap" }}>Basin</span>
                  <span style={{ fontSize: "13px", color: "#ffffff", fontWeight: 700 }}>{infoBox.basin}</span>
                </div>
              )}
              <div style={{ display: "flex", gap: "6px", marginBottom: "4px", alignItems: "baseline" }}>
                <span style={{ fontSize: "10px", color: "rgba(255,255,255,0.6)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", whiteSpace: "nowrap" }}>Cyclone</span>
                <span style={{ fontSize: "13px", color: "#ffffff", fontWeight: 700 }}>{infoBox.cyclone}</span>
              </div>
              <div style={{ marginTop: "6px", paddingTop: "6px", borderTop: "1px solid rgba(255,255,255,0.2)" }}>
                <div style={{ fontSize: "10px", color: "rgba(255,255,255,0.6)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "3px" }}>Period</div>
                <div style={{ fontSize: "11px", color: "rgba(255,255,255,0.9)", lineHeight: 1.5 }}>{infoBox.period}</div>
              </div>
            </div>
          )}

          {/* ── Legend ── */}
          <MapLegendControl
            sections={
              source === "JTWC"
                ? [{
                    title: "",
                    items: [{ label: "Observation Point", color: "#000", icon: (
                      <img src="/DATA/Cyclone/images/black.png" alt="point" style={{ width: "16px", height: "16px" }} />
                    ) }],
                  }]
                : [{
                    title: "",
                    shape: "line",
                    items: [{
                      label: tsrSelectedProducts.map((p) => ({ GH: "Gust History", WP: "Wind Prob.", RA: "Rainfall" }[p] || p)).join(" + ") || "TSR Data",
                      color: "#42a5f5",
                    }],
                  }]
            }
          />
        </MapContainer>
      </div>

      {/* ── JTWC Track table ── */}
      {source === "JTWC" && trackData.length > 0 && (
        <div style={{ marginTop: "20px", width: "100%" }}>
          <h3 style={{ textAlign: "center" }}>Extracted Cyclone Track Data</h3>
          <CommonTable
            tableData={trackData}
            columns={JTWC_TRACK_COLUMNS}
            tableName="HistoricalCyclone_Track"
            searchableKeys={["DTG", "Duration"]}
            searchPlaceholder="Search by DTG or Duration..."
            emptyMessage="No cyclone track data found."
            rowIdKey="rowKey"
            highlightRowKey={selectedTrackIndex}
            onRowClick={(row) => setSelectedTrackIndex(row.rowKey)}
          />
        </div>
      )}
    </div>
  );
};

export default HistoricalCyclone;
